/**
 * Mock handlers for tasks service
 * Simulates backend API responses with in-memory state persistence.
 *
 * Allineato al contratto BE docs/TASK_PROGETTO_BE.md (sezione 6):
 *  - tutti i task restituiti usano lo shape autosufficiente (location,
 *    project_detail, status_detail);
 *  - le colonne sono project-scoped e ripetono project_id/project_name;
 *  - le statistiche sono calcolate con aggregazione (non sul dataset paginato);
 *  - move è atomica e valida la coerenza project/status;
 *  - delete status richiede move-and-delete quando la colonna non è vuota;
 *    l'endpoint `?force=true` è rimosso dal nuovo contratto.
 */

import {
  getTasksState,
  setTasksState,
  getStatusesState,
  setStatusesState,
  getProjectsState,
  setProjectsState,
  getUsersState,
  getNextTaskId,
  getNextStatusId,
  getNextProjectId,
  serializeTask,
  serializeStatus,
  serializeProjectSummary,
  serializeProject,
} from '../data/tasks';
import { getRandomDelay } from '../config';

/**
 * Simulate async delay for realistic network behavior
 * @param {number} ms - Milliseconds to delay
 * @returns {Promise}
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Costruisce una risposta di errore nella shape envelope (BE 6.3 AI-P11).
 */
const buildError = (status, code, detail, context = {}, fieldErrors = null) => {
  const err = {
    response: {
      status,
      data: {
        code,
        detail,
        context,
        ...(fieldErrors ? { field_errors: fieldErrors } : {}),
      },
    },
  };
  return err;
};

/**
 * Priority mapping (frontend display format <-> API numeric format).
 * Backend usa 1=BASSA, 2=MEDIA, 3=ALTA.
 */
const PRIORITY_FROM_API = { 3: 'ALTA', 2: 'MEDIA', 1: 'BASSA', high: 'ALTA', medium: 'MEDIA', low: 'BASSA' };

const normalizePriority = (value) => {
  if (value === undefined || value === null) return 'MEDIA';
  if (typeof value === 'number') return PRIORITY_FROM_API[value] || 'MEDIA';
  if (typeof value === 'string') {
    if (PRIORITY_FROM_API[value]) return PRIORITY_FROM_API[value];
    if (['ALTA', 'MEDIA', 'BASSA'].includes(value)) return value;
  }
  return 'MEDIA';
};

const parseCsv = (value) => {
  if (value === undefined || value === null) return [];
  return String(value)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};

const todayStart = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

// ============================================================
// PROJECTS (BE 6.1)
// ============================================================

/**
 * Mock: GET /api/tasks/projects/?archived=<bool>&search=<text>
 * Lista non paginata, ordinata per position,name,id.
 */
export const mockGetTaskProjects = async (params = {}) => {
  await delay(getRandomDelay());

  let projects = [...getProjectsState()];

  if (params.archived !== undefined && params.archived !== null) {
    const wantArchived = params.archived === true || params.archived === 'true';
    projects = projects.filter((p) => p.is_archived === wantArchived);
  }
  if (params.search) {
    const q = String(params.search).toLowerCase();
    projects = projects.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q)
    );
  }

  projects.sort((a, b) => a.position - b.position || a.name.localeCompare(b.name) || a.id - b.id);
  return { data: projects.map((p) => serializeProject(p)) };
};

/**
 * Mock: GET /api/tasks/projects/{id}/ — dettaglio con colonne.
 */
export const mockGetTaskProject = async (id) => {
  await delay(getRandomDelay());
  const project = getProjectsState().find((p) => p.id === parseInt(id));
  if (!project) throw buildError(404, 'project_not_found', 'Progetto non trovato.', { project_id: parseInt(id) });
  return { data: serializeProject(project, { includeColumns: true }) };
};

const DEFAULT_BASE_COLUMNS = [
  { name: 'Da fare', color: '#8b5cf6' },
  { name: 'In corso', color: '#3b82f6' },
  { name: 'Completato', color: '#22c55e' },
];

/**
 * Mock: POST /api/tasks/projects/
 * Crea progetto e colonne in una transazione atomica.
 * Payload: { name, description?, color?, icon?, columns?, copy_columns_from_project_id? }
 */
export const mockCreateTaskProject = async (data) => {
  await delay(getRandomDelay());

  const name = (data?.name || '').trim();
  if (!name) {
    throw buildError(400, 'validation_error', 'Il nome del progetto è obbligatorio.', {}, { name: ['Il nome del progetto è obbligatorio.'] });
  }

  const existing = getProjectsState();
  const normalized = name.casefold ? name.casefold() : name.toLowerCase();
  const duplicate = existing.find((p) => p.name.casefold
    ? p.name.casefold() === normalized
    : p.name.toLowerCase() === normalized);
  if (duplicate) {
    throw buildError(409, 'duplicate_project_name', 'Esiste già un progetto con questo nome.', { name });
  }

  // Mutuamente esclusivi: columns OPPURE copy_columns_from_project_id.
  const hasColumns = Array.isArray(data.columns) && data.columns.length > 0;
  const hasCopy = data.copy_columns_from_project_id !== undefined && data.copy_columns_from_project_id !== null;
  if (hasColumns && hasCopy) {
    throw buildError(400, 'validation_error', 'Specifica colonne oppure copia da progetto, non entrambi.', {}, { columns: ['Incompatibile con copy_columns_from_project_id.'] });
  }

  const projectId = getNextProjectId();
  const maxPosition = Math.max(...existing.map((p) => p.position), -1);
  const now = new Date().toISOString();
  const newProject = {
    id: projectId,
    name,
    description: data.description || '',
    color: data.color || '#7b3fff',
    icon: data.icon || '',
    position: maxPosition + 10,
    is_archived: false,
    archived_at: null,
    created_at: now,
    updated_at: now,
  };

  // Risolvi colonne
  let columnsSpec = DEFAULT_BASE_COLUMNS;
  if (hasCopy) {
    const srcProjectId = parseInt(data.copy_columns_from_project_id);
    const srcProject = existing.find((p) => p.id === srcProjectId);
    if (!srcProject) {
      throw buildError(404, 'project_not_found', 'Progetto sorgente non trovato.', { project_id: srcProjectId });
    }
    const srcCols = getStatusesState().filter((s) => s.project_id === srcProjectId);
    columnsSpec = srcCols
      .sort((a, b) => a.order - b.order)
      .map((c) => ({ name: c.name, color: c.color, is_default: c.is_default }));
  } else if (hasColumns) {
    columnsSpec = data.columns.map((c) => ({ name: c.name, color: c.color || '#8b5cf6', is_default: c.is_default }));
  }

  // Valida nomi unici (case-insensitive, trim)
  const seen = new Set();
  for (const c of columnsSpec) {
    const k = (c.name || '').trim().toLowerCase();
    if (!k) throw buildError(400, 'column_name_required', 'Il nome della colonna è obbligatorio.', {}, { columns: ['Nome colonna vuoto.'] });
    if (seen.has(k)) throw buildError(409, 'duplicate_column_name', 'Esiste già una colonna con questo nome nel progetto.', { project_id: projectId });
    seen.add(k);
  }

  // Crea progetto + colonne atomicamente
  const statuses = getStatusesState();
  const defaultIdx = columnsSpec.findIndex((c) => c.is_default);
  const newStatuses = columnsSpec.map((c, idx) => {
    const newId = getNextStatusId();
    return {
      id: newId,
      key: c.name.toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, ''),
      name: c.name.trim(),
      color: c.color || '#8b5cf6',
      order: (idx + 1) * 10,
      is_active: true,
      is_default: defaultIdx === idx || (defaultIdx === -1 && idx === 0),
      project_id: projectId,
      project_name: name,
    };
  });

  setProjectsState([...existing, newProject]);
  setStatusesState([...statuses, ...newStatuses]);

  return { data: serializeProject(newProject, { includeColumns: true }) };
};

/**
 * Mock: PATCH /api/tasks/projects/{id}/ — solo metadati.
 */
export const mockUpdateTaskProject = async (id, data) => {
  await delay(getRandomDelay());
  const projects = getProjectsState();
  const idx = projects.findIndex((p) => p.id === parseInt(id));
  if (idx === -1) throw buildError(404, 'project_not_found', 'Progetto non trovato.', { project_id: parseInt(id) });
  if (projects[idx].is_archived) {
    throw buildError(409, 'project_archived', 'Il progetto è archiviato: ripristinalo prima di modificarlo.', { project_id: parseInt(id) });
  }

  if (data.name !== undefined) {
    const newName = String(data.name).trim();
    if (!newName) throw buildError(400, 'validation_error', 'Il nome del progetto è obbligatorio.', {}, { name: ['Il nome è obbligatorio.'] });
    const normalized = newName.toLowerCase();
    const dup = projects.find((p) => p.id !== projects[idx].id && p.name.toLowerCase() === normalized);
    if (dup) throw buildError(409, 'duplicate_project_name', 'Esiste già un progetto con questo nome.', { name: newName });
  }

  const updated = {
    ...projects[idx],
    name: data.name !== undefined ? String(data.name).trim() : projects[idx].name,
    description: data.description !== undefined ? data.description : projects[idx].description,
    color: data.color !== undefined ? data.color : projects[idx].color,
    icon: data.icon !== undefined ? data.icon : projects[idx].icon,
    updated_at: new Date().toISOString(),
  };
  const next = [...projects];
  next[idx] = updated;
  setProjectsState(next);

  // Propaga eventuale rename alle colonne (project_name).
  if (data.name !== undefined) {
    const statuses = getStatusesState();
    setStatusesState(
      statuses.map((s) => (s.project_id === updated.id ? { ...s, project_name: updated.name } : s))
    );
  }

  return { data: serializeProject(updated, { includeColumns: true }) };
};

/**
 * Mock: POST /api/tasks/projects/{id}/archive/ (idempotente).
 */
export const mockArchiveTaskProject = async (id) => {
  await delay(getRandomDelay());
  const projects = getProjectsState();
  const idx = projects.findIndex((p) => p.id === parseInt(id));
  if (idx === -1) throw buildError(404, 'project_not_found', 'Progetto non trovato.', { project_id: parseInt(id) });

  const updated = {
    ...projects[idx],
    is_archived: true,
    archived_at: projects[idx].archived_at || new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
  const next = [...projects];
  next[idx] = updated;
  setProjectsState(next);
  return { data: serializeProject(updated, { includeColumns: true }) };
};

/**
 * Mock: POST /api/tasks/projects/{id}/restore/ (idempotente).
 */
export const mockRestoreTaskProject = async (id) => {
  await delay(getRandomDelay());
  const projects = getProjectsState();
  const idx = projects.findIndex((p) => p.id === parseInt(id));
  if (idx === -1) throw buildError(404, 'project_not_found', 'Progetto non trovato.', { project_id: parseInt(id) });

  const updated = {
    ...projects[idx],
    is_archived: false,
    archived_at: null,
    updated_at: new Date().toISOString(),
  };
  const next = [...projects];
  next[idx] = updated;
  setProjectsState(next);
  return { data: serializeProject(updated, { includeColumns: true }) };
};

/**
 * Mock: POST /api/tasks/projects/{id}/reorder-columns/
 * Salva ordini 10,20,30,... in una transazione.
 */
export const mockReorderTaskColumns = async (projectId, data) => {
  await delay(getRandomDelay());
  const pid = parseInt(projectId);
  const project = getProjectsState().find((p) => p.id === pid);
  if (!project) throw buildError(404, 'project_not_found', 'Progetto non trovato.', { project_id: pid });

  const ids = Array.isArray(data?.column_ids) ? data.column_ids.map(Number) : [];
  const projectCols = getStatusesState().filter((s) => s.project_id === pid && s.is_active);
  if (ids.length !== projectCols.length) {
    throw buildError(400, 'validation_error', 'column_ids deve contenere tutti gli ID delle colonne attive del progetto.', { project_id: pid });
  }
  const projectColIds = new Set(projectCols.map((c) => c.id));
  for (const id of ids) {
    if (!projectColIds.has(id)) {
      throw buildError(400, 'validation_error', `La colonna ${id} non appartiene al progetto.`, { project_id: pid, column_id: id });
    }
  }
  if (new Set(ids).size !== ids.length) {
    throw buildError(400, 'validation_error', 'column_ids contiene duplicati.', { project_id: pid });
  }

  const statuses = getStatusesState();
  const reordered = statuses.map((s) => {
    const newOrderIdx = ids.indexOf(s.id);
    return newOrderIdx === -1 ? s : { ...s, order: (newOrderIdx + 1) * 10 };
  });
  setStatusesState(reordered);

  return { data: { ok: true, column_ids: ids } };
};

// ============================================================
// TASK CRUD (BE 6.3)
// ============================================================

/**
 * Mock: GET /api/tasks/tasks/
 * Applica i filtri del contratto nuovo (project, without_project, status,
 * status_ids, priorities, assigned_to_ids, created_by_ids, due_scopes,
 * is_completed, has_assignee, has_due_date, overdue, search, limit/offset).
 * Paginato con shape {count,next,previous,results}.
 */
export const mockGetTasks = async (params = {}) => {
  await delay(getRandomDelay());

  let tasks = [...getTasksState()];

  // Scope (mutuamente esclusivo)
  const hasProject = params.project !== undefined && params.project !== null && params.project !== '';
  const hasWithout = params.without_project === true || params.without_project === 'true';
  if (hasProject && hasWithout) {
    throw buildError(400, 'validation_error', 'Specifica project oppure without_project, non entrambi.', {});
  }
  if (hasProject) {
    const pid = parseInt(params.project);
    tasks = tasks.filter((t) => t.project === pid);
  } else if (hasWithout) {
    tasks = tasks.filter((t) => t.project === null || t.project === undefined);
  }

  // status exact (legacy) e status_ids CSV
  if (params.status !== undefined && params.status !== null && params.status !== '') {
    tasks = tasks.filter((t) => t.status === parseInt(params.status));
  }
  if (params.status_ids) {
    const ids = parseCsv(params.status_ids).map(Number);
    tasks = tasks.filter((t) => t.status !== null && t.status !== undefined && ids.includes(t.status));
  }

  // priorities CSV (1,2,3 numerico backend)
  if (params.priorities) {
    const wanted = parseCsv(params.priorities).map((v) => PRIORITY_FROM_API[v] || v);
    tasks = tasks.filter((t) => wanted.includes(t.priority));
  }

  if (params.assigned_to_ids) {
    const ids = parseCsv(params.assigned_to_ids).map(Number);
    tasks = tasks.filter((t) => (t.assigned_to_ids || []).some((uid) => ids.includes(uid)));
  }
  if (params.created_by_ids) {
    const ids = parseCsv(params.created_by_ids).map(Number);
    const byName = getUsersState().filter((u) => ids.includes(u.id)).map((u) => u.username);
    tasks = tasks.filter((t) => t.created_by && byName.includes(t.created_by));
  }

  // due_scopes CSV: today|week|overdue
  if (params.due_scopes) {
    const scopes = parseCsv(params.due_scopes);
    const today = todayStart();
    const weekEnd = new Date(today);
    weekEnd.setDate(weekEnd.getDate() + 7);
    tasks = tasks.filter((t) => {
      if (!t.due_date) return false;
      const due = new Date(t.due_date);
      due.setHours(0, 0, 0, 0);
      return scopes.some((scope) => {
        if (scope === 'today') return due.getTime() === today.getTime();
        if (scope === 'week') return due >= today && due <= weekEnd;
        if (scope === 'overdue') return due < today && !t.is_completed;
        return false;
      });
    });
  }

  if (params.is_completed !== undefined && params.is_completed !== null && params.is_completed !== '') {
    const want = params.is_completed === true || params.is_completed === 'true';
    tasks = tasks.filter((t) => Boolean(t.is_completed) === want);
  }
  if (params.has_assignee !== undefined && params.has_assignee !== null && params.has_assignee !== '') {
    const want = params.has_assignee === true || params.has_assignee === 'true';
    tasks = tasks.filter((t) => ((t.assigned_to_ids || []).length > 0) === want);
  }
  if (params.has_due_date !== undefined && params.has_due_date !== null && params.has_due_date !== '') {
    const want = params.has_due_date === true || params.has_due_date === 'true';
    tasks = tasks.filter((t) => Boolean(t.due_date) === want);
  }
  if (params.overdue === true || params.overdue === 'true') {
    const today = todayStart();
    tasks = tasks.filter((t) => t.due_date && new Date(t.due_date) < today && !t.is_completed);
  }

  if (params.search) {
    const q = String(params.search).toLowerCase();
    tasks = tasks.filter(
      (t) => t.title.toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q)
    );
  }

  // Ordina per (status order, position) in caso di progetto; altrimenti per updated_at desc.
  const statuses = getStatusesState();
  if (hasProject) {
    const orderOf = (sid) => statuses.find((s) => s.id === sid)?.order ?? Number.MAX_SAFE_INTEGER;
    tasks.sort((a, b) => orderOf(a.status) - orderOf(b.status) || (a.position ?? 0) - (b.position ?? 0));
  } else {
    tasks.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
  }

  const totalCount = tasks.length;
  const limit = params.limit !== undefined && params.limit !== null ? parseInt(params.limit) : 100;
  const offset = params.offset !== undefined && params.offset !== null ? parseInt(params.offset) : 0;
  const paged = tasks.slice(offset, offset + limit);

  return {
    data: {
      count: totalCount,
      next: offset + limit < totalCount ? `?limit=${limit}&offset=${offset + limit}` : null,
      previous: offset > 0 ? `?limit=${limit}&offset=${Math.max(0, offset - limit)}` : null,
      results: paged.map(serializeTask),
    },
  };
};

/**
 * Mock: GET /api/tasks/tasks/{id}/
 */
export const mockGetTaskById = async (id) => {
  await delay(getRandomDelay());
  const task = getTasksState().find((t) => t.id === parseInt(id));
  if (!task) throw buildError(404, 'not_found', 'Task non trovato.', { task_id: parseInt(id) });
  return { data: serializeTask(task) };
};

/**
 * Mock: POST /api/tasks/tasks/
 * Crea un task. Regole (BE 6.3):
 *  - project=null richiede status=null (inbox);
 *  - con progetto attivo e status omesso, usa default/prima colonna del progetto;
 *  - status esplicito deve appartenere al progetto.
 */
export const mockCreateTask = async (data) => {
  await delay(getRandomDelay());

  const tasks = getTasksState();
  const newId = getNextTaskId();
  const now = new Date().toISOString();

  const project = data.project !== undefined ? data.project : null;
  const statusRaw = data.status !== undefined ? data.status : undefined;
  const statuses = getStatusesState();

  // Valida coerenza project/status
  let finalProject = project;
  let finalStatus;

  if (project === null) {
    if (statusRaw !== undefined && statusRaw !== null) {
      throw buildError(400, 'project_required_for_status', 'Un task senza progetto non può avere una colonna.', { project_id: null });
    }
    finalStatus = null;
  } else {
    const pid = parseInt(project);
    const proj = getProjectsState().find((p) => p.id === pid);
    if (!proj) throw buildError(404, 'project_not_found', 'Progetto non trovato.', { project_id: pid });
    if (proj.is_archived) throw buildError(409, 'project_archived', 'Il progetto è archiviato.', { project_id: pid });

    const projectCols = statuses.filter((s) => s.project_id === pid && s.is_active);
    if (projectCols.length === 0) {
      throw buildError(400, 'status_required_for_project', 'Il progetto non ha colonne attive.', { project_id: pid });
    }

    if (statusRaw === undefined || statusRaw === null) {
      // default o prima per ordine
      const def = projectCols.find((c) => c.is_default) || projectCols.slice().sort((a, b) => a.order - b.order)[0];
      finalStatus = def.id;
    } else {
      const sid = parseInt(statusRaw);
      const col = statuses.find((s) => s.id === sid);
      if (!col) throw buildError(404, 'status_not_found', 'Colonna non trovata.', { status_id: sid });
      if (!col.is_active) throw buildError(409, 'status_inactive', 'La colonna non è più utilizzabile.', { status_id: sid });
      if (col.project_id !== pid) {
        throw buildError(400, 'status_project_mismatch', 'La colonna non appartiene al progetto indicato.', {
          requested_project_id: pid,
          status_id: sid,
          actual_status_project_id: col.project_id,
        });
      }
      finalStatus = sid;
    }
    finalProject = pid;
  }

  // assigned_to_ids
  const assignedToIds = Array.isArray(data.assigned_to_ids)
    ? data.assigned_to_ids.map(Number)
    : [];

  // Posizione: append in fondo alla colonna (o alla inbox).
  const position = tasks
    .filter((t) => t.project === finalProject && t.status === finalStatus)
    .length;

  const newTask = {
    id: newId,
    title: String(data.title || '').trim(),
    description: data.description || '',
    project: finalProject,
    status: finalStatus,
    position,
    priority: normalizePriority(data.priority),
    due_date: data.due_date || null,
    category: data.category || '',
    assigned_to_ids: assignedToIds,
    attachments: [],
    is_completed: false,
    created_by: 'Current User',
    created_at: now.split('T')[0],
    updated_at: now.split('T')[0],
  };

  setTasksState([...tasks, newTask]);
  return { data: serializeTask(newTask) };
};

/**
 * Mock: PATCH /api/tasks/tasks/{id}/
 * Aggiorna metadati + (legacy) project/status se entrambi coerenti.
 * Per spostamenti il FE nuovo usa /move/.
 */
export const mockUpdateTask = async (id, data) => {
  await delay(getRandomDelay());

  const tasks = getTasksState();
  const idx = tasks.findIndex((t) => t.id === parseInt(id));
  if (idx === -1) throw buildError(404, 'not_found', 'Task non trovato.', { task_id: parseInt(id) });

  const existing = tasks[idx];

  // Cambio project/status via PATCH: richiede entrambi coerenti nello stesso payload.
  if (data.project !== undefined || data.status !== undefined) {
    const newProject = data.project !== undefined ? data.project : existing.project;
    const newStatus = data.status !== undefined ? data.status : existing.status;
    if (newProject === null) {
      if (newStatus !== null && newStatus !== undefined) {
        throw buildError(400, 'project_required_for_status', 'Un task senza progetto non può avere una colonna.', {});
      }
    } else if (newProject !== null && newStatus !== null && newStatus !== undefined) {
      const col = getStatusesState().find((s) => s.id === parseInt(newStatus));
      if (!col) throw buildError(404, 'status_not_found', 'Colonna non trovata.', { status_id: parseInt(newStatus) });
      if (col.project_id !== parseInt(newProject)) {
        throw buildError(400, 'status_project_mismatch', 'La colonna non appartiene al progetto indicato.', {});
      }
    }
  }

  const updated = {
    ...existing,
    title: data.title !== undefined ? String(data.title).trim() : existing.title,
    description: data.description !== undefined ? data.description : existing.description,
    project: data.project !== undefined ? (data.project === null ? null : parseInt(data.project)) : existing.project,
    status: data.status !== undefined ? (data.status === null ? null : parseInt(data.status)) : existing.status,
    priority: data.priority !== undefined ? normalizePriority(data.priority) : existing.priority,
    due_date: data.due_date !== undefined ? data.due_date : existing.due_date,
    category: data.category !== undefined ? data.category : existing.category,
    assigned_to_ids: data.assigned_to_ids !== undefined
      ? data.assigned_to_ids.map(Number)
      : existing.assigned_to_ids,
    is_completed: data.is_completed !== undefined ? Boolean(data.is_completed) : existing.is_completed,
    updated_at: new Date().toISOString().split('T')[0],
  };

  const next = [...tasks];
  next[idx] = updated;
  setTasksState(next);
  return { data: serializeTask(updated) };
};

/**
 * Mock: POST /api/tasks/tasks/{id}/move/
 * Spostamento atomico: cambio progetto/colonna/posizione, oppure move-to-inbox.
 * Valida expected_source se presenti.
 */
export const mockMoveTask = async (id, data) => {
  await delay(getRandomDelay());

  const tid = parseInt(id);
  const tasks = getTasksState();
  const idx = tasks.findIndex((t) => t.id === tid);
  if (idx === -1) throw buildError(404, 'not_found', 'Task non trovato.', { task_id: tid });

  const task = tasks[idx];

  // Precondizioni source opzionali
  if (data.expected_source_project !== undefined && data.expected_source_project !== null) {
    const expectedProj = data.expected_source_project === null ? null : parseInt(data.expected_source_project);
    if (task.project !== expectedProj) {
      throw buildError(409, 'task_context_changed', 'Il task è stato modificato da un altro client.', {
        current_project: serializeProjectSummary(task.project),
        current_status: serializeStatus(task.status),
      });
    }
  }
  if (data.expected_source_status !== undefined && data.expected_source_status !== null) {
    const expectedStatus = data.expected_source_status === null ? null : parseInt(data.expected_source_status);
    if (task.status !== expectedStatus) {
      throw buildError(409, 'task_context_changed', 'Il task è stato modificato da un altro client.', {
        current_project: serializeProjectSummary(task.project),
        current_status: serializeStatus(task.status),
      });
    }
  }

  const destProject = data.project === undefined ? task.project : (data.project === null ? null : parseInt(data.project));
  const destStatus = data.status === undefined ? task.status : (data.status === null ? null : parseInt(data.status));
  const position = typeof data.position === 'number' ? data.position : 0;

  // Coerenza destinazione
  if (destProject === null) {
    if (destStatus !== null) {
      throw buildError(400, 'project_required_for_status', 'Un task senza progetto non può avere una colonna.', {});
    }
  } else {
    const proj = getProjectsState().find((p) => p.id === destProject);
    if (!proj) throw buildError(404, 'project_not_found', 'Progetto non trovato.', { project_id: destProject });
    if (proj.is_archived) throw buildError(409, 'project_archived', 'Il progetto è archiviato.', { project_id: destProject });
    if (destStatus === null) {
      throw buildError(400, 'status_required_for_project', 'Il task di progetto richiede una colonna.', { project_id: destProject });
    }
    const col = getStatusesState().find((s) => s.id === destStatus);
    if (!col) throw buildError(404, 'status_not_found', 'Colonna non trovata.', { status_id: destStatus });
    if (!col.is_active) throw buildError(409, 'status_inactive', 'La colonna non è più utilizzabile.', { status_id: destStatus });
    if (col.project_id !== destProject) {
      throw buildError(400, 'status_project_mismatch', 'La colonna non appartiene al progetto indicato.', {
        requested_project_id: destProject,
        status_id: destStatus,
        actual_status_project_id: col.project_id,
      });
    }
  }

  // Riorganizza posizioni sorgente/destinazione.
  const next = [...tasks];
  // Rimuovi task corrente dalla sua posizione originale
  const sourceSame = task.project === destProject && task.status === destStatus;
  const others = next.filter((t) => t.id !== tid);

  // Aggiorna posizioni del bucket destinazione (senza il task spostato)
  if (destProject !== null) {
    const dstBucket = others
      .filter((t) => t.project === destProject && t.status === destStatus)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const clampPos = Math.max(0, Math.min(position, dstBucket.length));
    dstBucket.splice(clampPos, 0, { ...task, project: destProject, status: destStatus, position: clampPos });
    dstBucket.forEach((t, i) => {
      const tIdx = next.findIndex((x) => x.id === t.id);
      if (tIdx !== -1) next[tIdx] = { ...next[tIdx], position: i };
    });
  } else {
    const inboxBucket = others.filter((t) => t.project === null);
    const clampPos = Math.max(0, Math.min(position, inboxBucket.length));
    inboxBucket.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    inboxBucket.splice(clampPos, 0, { ...task, project: null, status: null, position: clampPos });
    inboxBucket.forEach((t, i) => {
      const tIdx = next.findIndex((x) => x.id === t.id);
      if (tIdx !== -1) next[tIdx] = { ...next[tIdx], position: i };
    });
  }

  // Riscrivi posizioni sorgente (se diversa da dest)
  if (!sourceSame) {
    const srcBucket = others
      .filter((t) => t.project === task.project && t.status === task.status)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    srcBucket.forEach((t, i) => {
      const tIdx = next.findIndex((x) => x.id === t.id);
      if (tIdx !== -1) next[tIdx] = { ...next[tIdx], position: i };
    });
  }

  const updatedTaskIdx = next.findIndex((t) => t.id === tid);
  next[updatedTaskIdx] = {
    ...next[updatedTaskIdx],
    project: destProject,
    status: destStatus,
    updated_at: new Date().toISOString().split('T')[0],
  };
  setTasksState(next);

  return { data: serializeTask(next[updatedTaskIdx]) };
};

/**
 * Mock: PATCH /api/tasks/tasks/{id}/toggle-completed/
 */
export const mockToggleTaskCompleted = async (id) => {
  await delay(getRandomDelay());
  const tasks = getTasksState();
  const idx = tasks.findIndex((t) => t.id === parseInt(id));
  if (idx === -1) throw buildError(404, 'not_found', 'Task non trovato.', { task_id: parseInt(id) });
  const updated = {
    ...tasks[idx],
    is_completed: !tasks[idx].is_completed,
    updated_at: new Date().toISOString().split('T')[0],
  };
  const next = [...tasks];
  next[idx] = updated;
  setTasksState(next);
  return serializeTask(updated);
};

/**
 * Mock: DELETE /api/tasks/tasks/{id}/
 */
export const mockDeleteTask = async (id) => {
  await delay(getRandomDelay());
  const tasks = getTasksState();
  const newTasks = tasks.filter((t) => t.id !== parseInt(id));
  if (tasks.length === newTasks.length) {
    throw buildError(404, 'not_found', 'Task non trovato.', { task_id: parseInt(id) });
  }
  setTasksState(newTasks);
  return { data: null };
};

// ============================================================
// SUMMARY (BE 6.4)
// ============================================================

/**
 * Mock: GET /api/tasks/tasks/summary/?project=<id>|without_project=true
 * Calcolato su tutto lo scope (non sul dataset paginato).
 */
export const mockGetTaskSummary = async (params = {}) => {
  await delay(getRandomDelay());

  let tasks = [...getTasksState()];
  const hasProject = params.project !== undefined && params.project !== null && params.project !== '';
  const hasWithout = params.without_project === true || params.without_project === 'true';
  if (hasProject && hasWithout) {
    throw buildError(400, 'validation_error', 'Specifica project oppure without_project, non entrambi.', {});
  }
  if (!hasProject && !hasWithout) {
    throw buildError(400, 'validation_error', 'summary richiede uno scope project oppure without_project.', {});
  }
  if (hasProject) {
    const pid = parseInt(params.project);
    tasks = tasks.filter((t) => t.project === pid);
  } else {
    tasks = tasks.filter((t) => t.project === null || t.project === undefined);
  }

  const today = todayStart();
  const priorityCount = { high: 0, medium: 0, low: 0 };
  let completed = 0;
  let overdue = 0;

  for (const t of tasks) {
    if (t.priority === 'ALTA') priorityCount.high += 1;
    else if (t.priority === 'MEDIA') priorityCount.medium += 1;
    else if (t.priority === 'BASSA') priorityCount.low += 1;
    if (t.is_completed) completed += 1;
    if (t.due_date && !t.is_completed) {
      const due = new Date(t.due_date);
      due.setHours(0, 0, 0, 0);
      if (due < today) overdue += 1;
    }
  }

  return {
    data: {
      total: tasks.length,
      completed,
      overdue,
      priority: priorityCount,
    },
  };
};

// ============================================================
// TASK STATUSES (BE 6.2)
// ============================================================

/**
 * Mock: GET /api/tasks/task-statuses/?project=<id>
 * Senza project restituisce tutte le colonne visibili (per widget).
 */
export const mockGetTaskStatuses = async (params = {}) => {
  await delay(getRandomDelay());
  let statuses = [...getStatusesState()];
  if (params.project !== undefined && params.project !== null && params.project !== '') {
    const pid = parseInt(params.project);
    statuses = statuses.filter((s) => s.project_id === pid);
  }
  if (params.is_active !== undefined && params.is_active !== null && params.is_active !== '') {
    const want = params.is_active === true || params.is_active === 'true';
    statuses = statuses.filter((s) => s.is_active === want);
  }
  statuses.sort((a, b) => a.order - b.order || a.id - b.id);
  return { data: statuses.map((s) => serializeStatus(s.id)) };
};

/**
 * Mock: GET /api/tasks/tasks/available_statuses/ — legacy alias.
 */
export const mockGetAvailableStatuses = async () => {
  await delay(getRandomDelay());
  const statuses = getStatusesState()
    .filter((s) => s.is_active)
    .sort((a, b) => a.order - b.order);
  return { data: statuses.map((s) => serializeStatus(s.id)) };
};

/**
 * Mock: POST /api/tasks/task-statuses/
 * Crea una colonna in un progetto.
 */
export const mockCreateTaskStatus = async (data) => {
  await delay(getRandomDelay());

  if (!data || data.project === undefined || data.project === null) {
    throw buildError(400, 'validation_error', 'La colonna richiede un progetto.', {}, { project: ['Obbligatorio.'] });
  }
  const pid = parseInt(data.project);
  const project = getProjectsState().find((p) => p.id === pid);
  if (!project) throw buildError(404, 'project_not_found', 'Progetto non trovato.', { project_id: pid });
  if (project.is_archived) throw buildError(409, 'project_archived', 'Il progetto è archiviato.', { project_id: pid });

  const name = (data.name || '').trim();
  if (!name) {
    throw buildError(400, 'column_name_required', 'Il nome della colonna è obbligatorio.', {}, { name: ['Il nome è obbligatorio.'] });
  }
  // duplicate normalized
  const normalized = name.toLowerCase();
  const dup = getStatusesState().find((s) => s.project_id === pid && s.name.toLowerCase() === normalized);
  if (dup) {
    throw buildError(409, 'duplicate_column_name', 'Esiste già una colonna con questo nome nel progetto.', { project_id: pid });
  }

  const statuses = getStatusesState();
  const newId = getNextStatusId();
  const projectCols = statuses.filter((s) => s.project_id === pid);
  const maxOrder = Math.max(...projectCols.map((s) => s.order), 0);
  const key = name.toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');

  // Se è la prima colonna del progetto, è default automaticamente.
  const isFirstColumn = projectCols.length === 0;

  const newStatus = {
    id: newId,
    key,
    name,
    color: data.color || '#8b5cf6',
    order: data.order !== undefined ? data.order : maxOrder + 10,
    is_active: data.is_active !== undefined ? data.is_active : true,
    is_default: isFirstColumn || data.is_default === true,
    project_id: pid,
    project_name: project.name,
  };
  setStatusesState([...statuses, newStatus]);
  return { data: serializeStatus(newStatus.id) };
};

/**
 * Mock: PATCH /api/tasks/task-statuses/{id}/
 */
export const mockUpdateTaskStatus = async (id, data) => {
  await delay(getRandomDelay());
  const statuses = getStatusesState();
  const idx = statuses.findIndex((s) => s.id === parseInt(id));
  if (idx === -1) throw buildError(404, 'status_not_found', 'Colonna non trovata.', { status_id: parseInt(id) });

  const existing = statuses[idx];
  const project = getProjectsState().find((p) => p.id === existing.project_id);
  if (project && project.is_archived) {
    throw buildError(409, 'project_archived', 'Il progetto è archiviato.', { project_id: existing.project_id });
  }

  if (data.name !== undefined) {
    const newName = String(data.name).trim();
    if (!newName) throw buildError(400, 'column_name_required', 'Il nome della colonna è obbligatorio.', {}, { name: ['Il nome è obbligatorio.'] });
    const normalized = newName.toLowerCase();
    const dup = statuses.find((s) => s.id !== existing.id && s.project_id === existing.project_id && s.name.toLowerCase() === normalized);
    if (dup) throw buildError(409, 'duplicate_column_name', 'Esiste già una colonna con questo nome nel progetto.', { project_id: existing.project_id });
  }

  const updated = {
    ...existing,
    name: data.name !== undefined ? String(data.name).trim() : existing.name,
    color: data.color !== undefined ? data.color : existing.color,
    order: data.order !== undefined ? data.order : existing.order,
    is_active: data.is_active !== undefined ? data.is_active : existing.is_active,
  };
  const next = [...statuses];
  next[idx] = updated;
  setStatusesState(next);
  return { data: serializeStatus(updated.id) };
};

/**
 * Mock: DELETE /api/tasks/task-statuses/{id}/
 * Elimina solo colonne vuote. Con task restituisce 409.
 * `?force=true` è RIMOSSO dal nuovo contratto (ignorato se presente).
 */
export const mockDeleteTaskStatus = async (id) => {
  await delay(getRandomDelay());
  const statuses = getStatusesState();
  const status = statuses.find((s) => s.id === parseInt(id));
  if (!status) throw buildError(404, 'status_not_found', 'Colonna non trovata.', { status_id: parseInt(id) });

  const projectCols = statuses.filter((s) => s.project_id === status.project_id);
  if (projectCols.length <= 1) {
    throw buildError(400, 'validation_error', 'Non puoi eliminare l\'unica colonna del progetto.', { project_id: status.project_id });
  }

  const tasksUsing = getTasksState().filter((t) => t.status === status.id);
  if (tasksUsing.length > 0) {
    throw buildError(409, 'column_has_tasks', 'La colonna contiene task: scegli una destinazione.', {
      tasks_count: tasksUsing.length,
    });
  }

  setStatusesState(statuses.filter((s) => s.id !== status.id));
  return { data: null };
};

/**
 * Mock: POST /api/tasks/task-statuses/{id}/move-and-delete/
 * Sposta tutti i task nella destinazione e cancella la colonna atomicamente.
 */
export const mockMoveAndDeleteTaskStatus = async (id, data) => {
  await delay(getRandomDelay());
  const sid = parseInt(id);
  const statuses = getStatusesState();
  const status = statuses.find((s) => s.id === sid);
  if (!status) throw buildError(404, 'status_not_found', 'Colonna non trovata.', { status_id: sid });

  if (!data || data.destination_status === undefined || data.destination_status === null) {
    throw buildError(400, 'validation_error', 'destination_status è obbligatorio.', {}, { destination_status: ['Obbligatorio.'] });
  }
  const destId = parseInt(data.destination_status);
  if (destId === sid) {
    throw buildError(400, 'validation_error', 'La destinazione deve essere diversa dalla colonna da eliminare.', {});
  }
  const dest = statuses.find((s) => s.id === destId);
  if (!dest) throw buildError(404, 'status_not_found', 'Colonna destinazione non trovata.', { status_id: destId });
  if (!dest.is_active) throw buildError(409, 'status_inactive', 'La destinazione non è più utilizzabile.', { status_id: destId });
  if (dest.project_id !== status.project_id) {
    throw buildError(400, 'status_project_mismatch', 'La destinazione deve appartenere allo stesso progetto.', {
      project_id: status.project_id,
    });
  }

  // Sposta i task e normalizza posizioni nella destinazione
  const tasks = getTasksState();
  const moved = tasks.map((t) => (t.status === sid ? { ...t, status: destId, project: dest.project_id } : t));
  // Ricalcola posizioni della destinazione
  const dstTasks = moved
    .filter((t) => t.status === destId)
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  dstTasks.forEach((t, i) => {
    const tIdx = moved.findIndex((x) => x.id === t.id);
    if (tIdx !== -1) moved[tIdx] = { ...moved[tIdx], position: i };
  });
  setTasksState(moved);
  setStatusesState(statuses.filter((s) => s.id !== sid));
  return { data: null };
};

/**
 * Mock: GET /api/tasks/task-statuses/{id}/in-use/
 */
export const mockCheckTaskStatusInUse = async (statusId) => {
  await delay(getRandomDelay());
  const count = getTasksState().filter((t) => t.status === parseInt(statusId)).length;
  return { data: { in_use: count > 0, task_count: count } };
};

// ============================================================
// TASK HISTORY (placeholder)
// ============================================================

export const mockGetTasksHistory = async () => {
  await delay(getRandomDelay());
  return { data: [] };
};

export const mockGetTaskHistory = async () => {
  await delay(getRandomDelay());
  return { data: [] };
};

// ============================================================
// ASSIGNABLE USERS (from users module)
// ============================================================

export const mockGetAssignableUsers = async () => {
  await delay(getRandomDelay());
  return { data: getUsersState() };
};

// ============================================================
// EXPORT ALL HANDLERS
// ============================================================

const tasksMockHandlers = {
  // Projects
  mockGetTaskProjects,
  mockGetTaskProject,
  mockCreateTaskProject,
  mockUpdateTaskProject,
  mockArchiveTaskProject,
  mockRestoreTaskProject,
  mockReorderTaskColumns,

  // Tasks CRUD + move + summary
  mockGetTasks,
  mockGetTaskById,
  mockCreateTask,
  mockUpdateTask,
  mockDeleteTask,
  mockMoveTask,
  mockToggleTaskCompleted,
  mockGetTaskSummary,

  // Task Statuses
  mockGetTaskStatuses,
  mockGetAvailableStatuses,
  mockCreateTaskStatus,
  mockUpdateTaskStatus,
  mockDeleteTaskStatus,
  mockMoveAndDeleteTaskStatus,
  mockCheckTaskStatusInUse,

  // Task History
  mockGetTasksHistory,
  mockGetTaskHistory,

  // Users (assignable)
  mockGetAssignableUsers,
};

export default tasksMockHandlers;
